import { formatMoney, intervalName } from '@/helpers/billing-format'

/**
 * How the plan picker and the change review write plans and what a change
 * would do. The billing API describes both; these turn that into what the
 * screens say. Amounts are integer cents.
 */

const FEATURE_LABELS = new Map([
    ['flag_lifecycle_management', 'Flag lifecycle management'],
    ['audit_log_access', 'Audit log'],
    ['advanced_reporting', 'Advanced reporting'],
    ['management_api_access', 'Management API'],
])

/**
 * @param {string} key - An entitlement key, e.g. `audit_log_access`
 * @returns {string} What the owner reads, e.g. "Audit log"
 */
export function featureLabel(key) {
    const label = FEATURE_LABELS.get(key)
    if (label) return label

    const words = String(key).replace(/_/g, ' ')
    return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The monthly active contexts a plan includes.
 *
 * @param {number|null} limit - The plan's allowance, or null when it has none
 * @returns {string} "25,000 monthly active contexts" or "Unlimited monthly active contexts"
 */
export function macsLabel(limit) {
    if (limit === null || limit === undefined) {
        return 'Unlimited monthly active contexts'
    }

    return `${limit.toLocaleString('en-US')} monthly active contexts`
}

/**
 * What a price costs a month, whatever it is billed at, for comparing plans.
 *
 * @param {{ amount: number, interval: string }|null} price
 * @returns {number} Cents, not rounded
 */
export function monthlyEquivalent(price) {
    if (!price) return 0

    return price.interval === 'year' ? price.amount / 12 : price.amount
}

/**
 * One price on a line.
 *
 * @param {{ amount: number, currency: string, interval: string }} price
 * @returns {string} "$49.00 a month" or "$490.00 a year"
 */
export function pricePeriod(price) {
    const unit = price.interval === 'year' ? 'year' : 'month'
    return `${formatMoney(price.amount, price.currency)} a ${unit}`
}

/**
 * The most an annual price saves against twelve months at the monthly price,
 * across the plans that have both. Nothing when no plan saves anything.
 *
 * @param {Array<{ prices: { month: object|null, year: object|null } }>} plans
 * @returns {number|null} A whole percentage
 */
export function annualSavingPercent(plans) {
    let best = 0

    for (const plan of plans) {
        const { month, year } = plan.prices
        if (!month || !year || month.amount <= 0) continue

        const saved = Math.round((1 - year.amount / (month.amount * 12)) * 100)
        if (saved > best) best = saved
    }

    return best > 0 ? best : null
}

// The states in which the plan page lets the owner start a paid plan again.
const REACTIVATABLE = ['suspended', 'cancelled']

/**
 * What choosing a plan does for this subscription: the change it starts, or
 * why it can't be chosen. `option` is a plan as the picker holds it
 * (`{ free, plan_version_ulid, name, price }`, where `price` is the one at the
 * interval being looked at, or null if the plan isn't sold at it).
 *
 * @param {object} option
 * @param {object} context
 * @param {object} context.subscription - From the billing summary
 * @param {object|null} context.scheduledChange - From the billing summary
 * @returns {{ state: 'current'|'scheduled'|'unavailable'|'choose', kind?: string, label: string, badge?: string, primary?: boolean }}
 *   `primary` is false for a choice the page shouldn't draw as its main action
 */
export function planChoice(option, { subscription, scheduledChange }) {
    const status = subscription.status
    const plan = subscription.plan
    const trialing = status === 'trialing'
    const scheduledCancel = scheduledChange?.kind === 'cancel'

    if (option.free) {
        if (status === 'free' || plan?.is_free) {
            return { state: 'current', label: 'Your plan', badge: 'Current' }
        }
        if (REACTIVATABLE.includes(status)) {
            return { state: 'unavailable', label: 'Not available' }
        }
        if (scheduledCancel) {
            return {
                state: 'scheduled',
                label: 'Scheduled',
                badge: 'Scheduled',
            }
        }

        return {
            state: 'choose',
            kind: 'cancel',
            label: trialing ? 'Choose Free' : 'Move to Free',
            primary: false,
        }
    }

    const price = option.price
    if (!price) {
        return { state: 'unavailable', label: 'Not sold at this length' }
    }

    // During a trial the plan that counts is the one the trial turns into.
    const heldPrice = trialing
        ? (subscription.trial?.converts_to?.price_ulid ?? plan?.price_ulid)
        : plan?.price_ulid

    if (price.price_ulid === heldPrice && !REACTIVATABLE.includes(status)) {
        return {
            state: 'current',
            label: trialing ? 'Your plan after the trial' : 'Your plan',
            badge: trialing ? 'After your trial' : 'Current',
        }
    }

    if (scheduledChange?.target?.price_ulid === price.price_ulid) {
        return { state: 'scheduled', label: 'Scheduled', badge: 'Scheduled' }
    }

    if (REACTIVATABLE.includes(status)) {
        return {
            state: 'choose',
            kind: 'reactivate',
            label: 'Reactivate on this plan',
        }
    }

    if (trialing) {
        return {
            state: 'choose',
            kind: 'change_plan',
            label: 'Choose this plan',
        }
    }

    // The same plan at the other length is a switch of billing, not a new plan.
    if (
        plan &&
        option.plan_version_ulid === plan.plan_version_ulid &&
        price.interval !== plan.interval
    ) {
        return {
            state: 'choose',
            kind: 'change_plan',
            label: `Switch to ${intervalName(price.interval)} billing`,
        }
    }

    const direction = monthlyEquivalent(price) - monthlyEquivalent(plan)
    let label = 'Switch to this plan'
    if (status === 'free' || plan?.is_free || direction > 0) label = 'Upgrade'
    else if (direction < 0) label = 'Downgrade'

    // Moving to a smaller plan is available, not the thing to do: it isn't
    // drawn as the page's main action.
    return {
        state: 'choose',
        kind: 'change_plan',
        label,
        ...(label === 'Downgrade' ? { primary: false } : {}),
    }
}

// Notices the review writes in its own words, so the API's are left out of the
// list: the features lost and the usage over the limit have their own rows,
// and the discount ending is the box the owner ticks.
const LOST_FEATURE_NOTICE = /^This plan does not include /
const OVER_LIMIT_NOTICE = /^Current usage exceeds the new plan's limit/
const DISCOUNT_ENDS_NOTICE = /will end on this plan\.$/

/**
 * What a preview says, as the review step shows it.
 *
 * @param {object} preview - `POST billing/changes/preview` answered with it
 * @param {object} context
 * @param {object|null} [context.target] - The price the change is for
 * @param {object|null} [context.scheduledChange] - The change scheduled now
 * @returns {object} The view of the change
 */
export function describeChange(
    preview,
    { target = null, scheduledChange = null } = {}
) {
    const current = preview.current ?? {}
    const proposed = preview.proposed ?? {}
    const money = preview.money ?? null
    const entitlements = preview.entitlements ?? null

    // Money means it happens now. A scheduled change has none and an
    // effective date, and choosing a plan during a trial has neither.
    let timing = 'scheduled'
    if (money) timing = 'now'
    else if (!preview.effective_at && current.status === 'trialing')
        timing = 'trial'

    const newPeriod =
        !!money &&
        !!current.billing_anchor &&
        proposed.billing_anchor !== current.billing_anchor

    return {
        action: preview.action,
        timing,
        effectiveAt: preview.effective_at ?? null,
        today: money
            ? describeToday(money, preview, { target, newPeriod })
            : null,
        next: describeNext(preview, { timing, target }),
        lostFeatures: (entitlements?.lost ?? []).map(featureLabel),
        overLimit: entitlements?.mac_usage?.over_proposed_limit
            ? {
                  usage: entitlements.mac_usage.current_period_usage,
                  limit: entitlements.mac_usage.proposed_limit,
              }
            : null,
        discountEnds: preview.requires_acknowledgement
            ? {
                  notice:
                      (preview.notices ?? []).find((n) =>
                          DISCOUNT_ENDS_NOTICE.test(n)
                      ) ?? 'Your discount will end on this plan.',
              }
            : null,
        notices: (preview.notices ?? []).filter(
            (notice) =>
                !LOST_FEATURE_NOTICE.test(notice) &&
                !OVER_LIMIT_NOTICE.test(notice) &&
                !DISCOUNT_ENDS_NOTICE.test(notice)
        ),
        replacesScheduled:
            preview.clears_scheduled_change && scheduledChange
                ? { effectiveAt: scheduledChange.effective_at }
                : null,
    }
}

function describeToday(money, preview, { target, newPeriod }) {
    const currency = money.currency
    const lines = []

    if (money.credit_line < 0) {
        lines.push({
            key: 'credit',
            label: 'Credit for unused time on your current plan',
            amount: money.credit_line,
        })
    }
    if (money.charge_line > 0) {
        const name = target?.plan_name ?? 'The new plan'
        lines.push({
            key: 'charge',
            label: newPeriod
                ? `${name} (${intervalName(target?.interval)})`
                : `${name} for the rest of this period`,
            amount: money.charge_line,
        })
    }
    if (money.tax > 0) {
        lines.push({ key: 'tax', label: 'Tax', amount: money.tax })
    }

    return {
        currency,
        lines,
        total: money.total ?? money.net,
        discountOff: preview.discount?.amount_off ?? 0,
        reverseCharge: !!money.reverse_charge,
    }
}

function describeNext(preview, { timing, target }) {
    const current = preview.current ?? {}
    const proposed = preview.proposed ?? {}

    if (preview.action === 'cancel') {
        return { kind: 'free', date: preview.effective_at }
    }

    if (timing === 'trial') {
        if (!target || target.is_free) {
            return { kind: 'free', date: current.trial_ends_at }
        }

        return {
            kind: 'first-invoice',
            date: current.trial_ends_at,
            amount: target.amount,
            currency: target.currency,
            interval: target.interval,
        }
    }

    if (timing === 'scheduled') {
        return {
            kind: 'moves',
            date: preview.effective_at,
            name: target?.plan_name ?? null,
            amount: proposed.amount,
            currency: proposed.currency,
            interval: proposed.interval,
        }
    }

    return {
        kind: 'renews',
        date: proposed.current_period_end,
        amount: proposed.amount,
        currency: proposed.currency,
        interval: proposed.interval,
    }
}

/**
 * What the owner can do about a change the API refused, by its `error_code`:
 * fix the thing it is missing, then look again.
 */
const REFUSAL_FIXES = new Map([
    ['card_required', { action: 'card', label: 'Add a card' }],
    [
        'billing_address_required',
        { action: 'details', label: 'Add a billing address' },
    ],
    [
        'upgrade_blocked_past_due',
        { action: 'card', label: 'Update card and pay' },
    ],
])

/**
 * @param {string|undefined} errorCode
 * @returns {{ action: 'card'|'details', label: string }|null}
 */
export function refusalFix(errorCode) {
    return REFUSAL_FIXES.get(errorCode) ?? null
}
